import classNames from "classnames";
import { useTranslations } from "next-intl";
import { useRef } from "react";

import { KikReviewsV2Rollup } from "~/shared/power-reviews-response.interface";

import styles from "./reviews-histogram.module.scss";
import tooltipStyles from "./reviews-tooltip.module.scss";

type PropsProductReviews = {
  rollup: KikReviewsV2Rollup;
  reviewsFilterByStars: (rating: number) => void;
  starsFilter?: number;
};

export default function ProductReviews(props: PropsProductReviews) {
  const t = useTranslations();
  const { rollup, reviewsFilterByStars, starsFilter } = props;
  const reviewsSectionRef = useRef<HTMLElement>(null);

  const onHistogramBarClick = (ratingNumber: number) => {
    reviewsSectionRef.current?.offsetTop &&
      window.scrollTo({
        top: reviewsSectionRef.current.offsetTop,
        behavior: "smooth",
      });
    reviewsFilterByStars(ratingNumber);
  };

  const starLabel = (stars: number) => {
    return t(`reviews.histogramStar${stars > 1 ? "s" : ""}`, { stars: stars });
  };

  const ariaLabel = (stars: number, thisRatingReviewsCount: number, isSelected: boolean) => {
    const starsAndReviewsTotal = `${starLabel(stars)} (${thisRatingReviewsCount} ${t("generic.reviews")}).`;
    const labelKey = isSelected ? "reviews.histogramFilter" : "reviews.histogramTooltip";
    const actionDescription = t(labelKey, { rating: stars });
    return `${starsAndReviewsTotal} ${actionDescription}`;
  };

  return (
    <div className={styles.reviewsSnapshotHistogram}>
      <h3 className={styles.reviewsSnapshotHistogramTitle}>{t("reviews.histogramTitle")}</h3>
      <ul className={styles.reviewsHistogramList}>
        {Array.from({ length: 5 })
          .map((v, i) => {
            const thisRatingReviewsCount = rollup.rating_histogram[i];
            const starCount = i + 1;
            const isSelected = starsFilter === starCount;
            return (
              <li key={i + (rollup.name || "") + rollup.locale}>
                <button
                  type="button"
                  className={classNames(styles.reviewsHistogramButton, tooltipStyles.reviewsTooltipContainer, {
                    [styles.reviewsHistogramButtonSelected]: isSelected,
                  })}
                  onClick={() => onHistogramBarClick(starCount)}
                  disabled={thisRatingReviewsCount === 0}
                  aria-disabled={thisRatingReviewsCount === 0}
                  aria-label={ariaLabel(starCount, thisRatingReviewsCount, isSelected)}
                >
                  <span className={styles.reviewsHistogramLabel}>{starLabel(starCount)}</span>
                  <span className={styles.reviewsHistogramBarContainer}>
                    <span
                      className={classNames(styles.reviewsHistogramBar, tooltipStyles.reviewsTooltip)}
                      data-tooltip={t("reviews.histogramTooltip", { rating: starCount })}
                    >
                      <span
                        className={styles.reviewsHistogramBarFill}
                        style={{
                          width: `${(thisRatingReviewsCount / rollup.review_count) * 100}%`,
                        }}
                      ></span>
                    </span>
                  </span>
                  <span className={styles.reviewsHistogramLabel}>{thisRatingReviewsCount}</span>
                </button>
              </li>
            );
          })
          .reverse()}
      </ul>
    </div>
  );
}
